/**
 * Resolves the backend base URL for public (unauthenticated) vistory calls.
 *
 * Prefers runtime `window.__ENV__` values, falling back to build-time env.
 *
 * @returns The API base URL, or an empty string when none is configured.
 */
function resolvePublicApiBase(): string {
  const windowEnv = (window as any).__ENV__;
  return (
    windowEnv?.REACT_APP_API_URL ||
    windowEnv?.REACT_APP_HOUSIEN_BACKEND_URL ||
    import.meta.env.REACT_APP_API_URL ||
    ''
  );
}

export interface VistoryReport {
  vistory_id: string;
  title: string;
  description: string;
  created_at: string;
  /**
   * Exactly what `build_photo_urls` returns: a signed `url` and the
   * filename. It never sent `key`, and never sent `vistory_id` either.
   */
  photos: Array<{ url: string; filename: string }>;
}

interface VerifyResponse {
  valid: boolean;
  vistory_id: string;
}

/**
 * Performs a JSON fetch against the public vistory API.
 *
 * @typeParam T - Expected response body shape on success.
 * @param path - Request path appended to the resolved API base.
 * @param options - Optional fetch options (method, body, headers).
 * @returns An object with `ok`, HTTP `status`, and parsed `data` when successful.
 */
async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<{ ok: boolean; status: number; data?: T }> {
  const base = resolvePublicApiBase();
  const res = await fetch(`${base}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) return { ok: false, status: res.status };
  const data = await res.json();
  return { ok: true, status: res.status, data };
}

export const PublicVistoryService = {
  async verifyCode(token: string, code: string) {
    return request<VerifyResponse>(`/public/vistory/${token}/verify`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  async getReport(token: string, code: string) {
    return request<VistoryReport>(`/public/vistory/${token}/report`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  async approve(token: string, code: string) {
    return request<void>(`/public/vistory/${token}/approve`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  async reject(token: string, code: string, reason: string, appointments?: Array<{ date: string; time: string; note?: string }>) {
    return request<void>(`/public/vistory/${token}/reject`, {
      method: 'POST',
      body: JSON.stringify({ code, reason, appointments }),
    });
  },
};
